import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Button } from '../../components/Shared/Button';
import { Card } from '../../components/Shared/Card';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import { useMeSupportStore } from '../../store/useMeSupportStore';
import { useAuthStore } from '../../store/useAuthStore';
import {
  SupportTicketStatus,
  SupportTicketType,
  type SupportTicketDto,
} from '../../api/types/admin';
import { formatDateTime } from '../../utils/dateUtils';
import { markAsRead } from '../../utils/supportReadMarker';

const TYPE_KEY: Record<number, string> = {
  [SupportTicketType.Question]: 'question',
  [SupportTicketType.Problem]: 'problem',
  [SupportTicketType.NewTenantRequest]: 'question',
  [SupportTicketType.MeetingRequest]: 'meetingRequest',
  [SupportTicketType.Other]: 'other',
};

const STATUS_KEY: Record<number, string> = {
  [SupportTicketStatus.New]: 'new',
  [SupportTicketStatus.InProgress]: 'inProgress',
  [SupportTicketStatus.Closed]: 'closed',
};

function statusColor(s: SupportTicketStatus | undefined): 'gray' | 'blue' | 'green' {
  if (s === SupportTicketStatus.Closed) return 'gray';
  if (s === SupportTicketStatus.InProgress) return 'blue';
  return 'green';
}

export default function MeSupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['support', 'common']);
  const tenantId = useAuthStore((s) => s.employee?.tenantId ?? '');

  const { saving, getById, reply } = useMeSupportStore();
  const [ticket, setTicket] = useState<SupportTicketDto | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const fetched = await getById(id);
      if (!cancelled) {
        setTicket(fetched);
        setLoaded(true);
        if (fetched?.id && tenantId) markAsRead(tenantId, fetched.id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, getById, tenantId]);

  if (!loaded) {
    return <p className="text-[13px] text-slate-500 text-center py-12">{t('common:actions.loading')}</p>;
  }

  if (!ticket || !ticket.id) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('support:errors.notFound')}
          action={
            <Link to="/support">
              <Button variant="secondary" leftIcon={<ArrowLeft className="w-3.5 h-3.5" />}>
                {t('support:actions.backToList')}
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  const isClosed = ticket.status === SupportTicketStatus.Closed;

  const handleReply = async () => {
    if (!ticket.id || !replyText.trim()) return;
    const ok = await reply(ticket.id, replyText.trim());
    if (ok) {
      setReplyText('');
      const fresh = await getById(ticket.id);
      setTicket(fresh);
      if (fresh?.id && tenantId) markAsRead(tenantId, fresh.id);
    }
  };

  const sortedReplies = [...(ticket.replies ?? [])].sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return da - db;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <Button
        variant="secondary"
        onClick={() => navigate('/support')}
        leftIcon={<ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />}
      >
        {t('support:actions.backToList')}
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900 truncate">
                {ticket.subject}
              </h1>
              <Badge>
                {t(`support:types.${TYPE_KEY[ticket.type ?? -1] ?? 'other'}`)}
              </Badge>
            </div>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('support:fields.createdAt')}: {formatDateTime(ticket.createdAt)}
            </p>
          </div>
          <Badge color={statusColor(ticket.status)}>
            {t(`support:status.${STATUS_KEY[ticket.status ?? -1] ?? 'new'}`)}
          </Badge>
        </div>
      </div>

      <Card>
        <div className="kicker mb-2">{t('support:detail.originalMessage')}</div>
        <p className="text-[13px] text-slate-800 whitespace-pre-wrap">{ticket.message}</p>
      </Card>

      {sortedReplies.length > 0 && (
        <Card title={t('support:detail.history')} noPadding>
          <ul className="px-4 py-3 space-y-3">
            {sortedReplies.map((r) => {
              const fromAdmin = !!r.fromAdmin;
              return (
                <li key={r.id} className={`flex ${fromAdmin ? 'justify-start' : 'justify-end'}`}>
                  <div
                    className={
                      fromAdmin
                        ? 'max-w-[85%] border-l-2 border-[#00A871] bg-emerald-50/60 rounded-md px-3 py-2'
                        : 'max-w-[85%] bg-slate-50 rounded-md px-3 py-2'
                    }
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[12px] font-medium text-slate-900">
                        {fromAdmin ? t('support:detail.from.admin') : t('support:detail.from.tenant')}
                      </span>
                      <span className="text-[11px] text-slate-500">{formatDateTime(r.createdAt)}</span>
                    </div>
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{r.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {isClosed ? (
        <Card>
          <p className="text-[13px] text-slate-600">{t('support:detail.closedHint')}</p>
        </Card>
      ) : (
        <Card>
          <div className="space-y-2">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={5}
              placeholder={t('support:detail.replyPlaceholder')}
              className="w-full px-3 py-2 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 resize-y"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleReply}
                disabled={saving || !replyText.trim()}
                leftIcon={<Send className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {t('support:actions.reply')}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
